// CACHE-BUST 20260517T154912 - duplicate refreshMockupImages import removed
// assets/wc-result.js
// Post-generation result rendering. CURRENTLY BUGGY (synchronous DOM thrash
// causes 8-15s freeze on throttled CPU) — Sub-project A rewrites showResult.
// Sub-project B just relocates the existing logic verbatim from monolith.
//
// Monolith line ranges lifted:
//   1279–1290   stashResult
//   3040–3111   updateResultPhase1_5
//   3117–3237   updateResultPhase2
//   5005–5095   refreshMockupImages (includes deferred preloadFramedMockups kick)
//   5110–5132   setSlideDeckVisibility (private helper; also in wc-selectors.js)
//   5176–5375   showResult (main body + _wcSafeCall + persistCurrentGen + renderGensStrip)
//   5381–5428   persistCurrentGen (module-private)
//   5432–5493   renderGensStrip (module-private)
//   5496–5580   switchToGen
//   5744–5862   bootstrapStateFromBubble
//
// Cross-cutting calls preserved verbatim via `typeof fn === 'function'` guards:
//   refreshActiveBranches, refreshSizeFramePills, refreshPricingDisplay,
//   applyPdpVisibility, updatePdpVariantState, refreshAtcReadyState,
//   startResolveRecoveryPolling, stopResolveRecoveryPolling,
//   initUploadZoneState, renderUnifiedGallery, refreshReopenPill,
//   applyFramedMockupToGallery, stopLoadingTicker, refreshProgressPill,
//   readPlaceholderUrls, setSlideIndex
//
// Functions NOT extracted here (belong to other modules):
//   applyCleanBreakNow / applyCleanBreakDeferred / flushDeferredSwap
//     → wc-upload.js / wc-init.js  (PDP swap chain; not result rendering)
//   addCurrentSelectionToCart / buildCurrentCartItem / wcCartAddWithRetry
//     → wc-upload.js  (cart submission)
//   refreshAtcReadyState / refreshAtcLabelForPreGen / applyPdpVisibility
//     → left in monolith (cross-cutting; called via typeof guard here)
//   bootstrapStateFromCurrentChildPdp / liveResolveCurrentVariantId
//     → wc-hydration.js  (hydration concerns)
//   maybeRecoverPendingGen / tryHydrateChildPdpFromUrl
//     → wc-hydration.js
//   startResolveRecoveryPolling / stopResolveRecoveryPolling
//     → left in monolith (cross-cutting poll loop)
//
// WC._currentResult and all other WC.* state slots are shared globals set
// by the monolith's IIFE wrapper. This module reads/writes them directly.

import { log, trace, track } from './wc77-analytics.js';
import { getModal, setModalState, syncModalAriaName } from './wc2-modal.js';
import { applyToReveal, getActiveVariant } from './wc77-experiments.js';
import { pickRevealFormat } from './wc-reveal-format.js';
import { raf, idle } from './wc2-dom.js';
import { stop as stopLoading } from './wc77-loading.js';
// 2026-05-19 — V2 namespace fix: persistCurrentGen was calling
// `window.WC.gens.getNamespaceKey` / `.add` / `.syncSaveToCloud`, but
// `window.WC.gens` is initialised as an empty object in theme.liquid and
// never wired up in V2 (the monolith's IIFE exposed them; V2 modules just
// import them directly). Result: every persistCurrentGen call threw on
// the undefined `getNamespaceKey` and bailed silently — localStorage gen
// store stayed empty, drop-zone State-B never had data to display.
// 2026-07-02 — phase-2 persistence parity fix: updateMockups is imported
// directly (same reason as the imports above — `window.WC.gens` is an empty
// object in wc77, so the old `WC.gens.updateMockups` guard was a silent
// no-op and champion gens lost phase-2 mockups on reload; the flows already
// import it directly, see wc77-flow-b.js / wc77-flow-c.js).
import { getNamespaceKey, getNamespaceType, add as gensAdd, syncSaveToCloud, updateMockups as gensUpdateMockups } from './wc2-state.js';
import {
  preloadFramedMockups,
  refreshActiveBranches,
  refreshSizeFramePills,
  refreshPricingDisplay,
  renderUnifiedGallery,
  updatePdpVariantState,
  installFramedGallerySrcGuard,
  teardownFramedGallerySrcGuard,
  setSlideIndex,
} from './wc77-selectors.js';
// NOTE: refreshMockupImages is DEFINED locally in this module (see line ~287)
// — do NOT import the wc-selectors version (duplicate identifier).
// 2026-05-19 — Audit fix: was importing from unversioned `./wc-pdp-swap.js`
// (wc7-era). That file's applyCleanBreakNow calls wc7-selectors.js's
// updatePdpVariantState, which has the original 2-branch (canvas/paint_kit)
// variant resolution bug. Repointed to the wc23 file (chain-current).
import { refreshReopenPill, refreshProgressPill, flushDeferredSwap } from './wc-pdp-swap-77.js';


// ─── stashResult ────────────────────────────────────────────────────────────
// Monolith lines 1279–1290. Saves last result so reopenLastResult can restore.

export function stashResult(generationId, orchResult) {
  window.WC._lastResult = {
    generationId: generationId,
    orchResult: orchResult,
    snapshot: JSON.parse(JSON.stringify(window.WC._currentResult)),
    stashedAt: Date.now(),
  };
  // Phase 16 — surface the reopen pill if modal is currently hidden.
  // Covers the case where the user minimized during generating + the
  // result completed while the modal stays hidden.
  try { refreshReopenPill(); } catch (e) { /* non-fatal */ }
}

// ─── updateResultPhase1_5 ────────────────────────────────────────────────────
// Monolith lines 3040–3111. Merges Phase 1.5 variant data into R (MERGE not
// OVERWRITE — see comment inside). Called by the upload orchestrator handler
// when the Phase 1.5 webhook fires.

export function updateResultPhase1_5(phase1_5) {
  var R = window.WC._currentResult;
  if (!R) {
    trace('phase1_5-skip', { reason: 'R-null' });
    log('updateResultPhase1_5: WC._currentResult is null; skipping');
    return;
  }
  var pCanvasKeys = (phase1_5 && phase1_5.canvas && phase1_5.canvas.shopify) ? Object.keys(phase1_5.canvas.shopify) : null;
  var pFramedKeys = (phase1_5 && phase1_5.canvasFramed && phase1_5.canvasFramed.shopify) ? Object.keys(phase1_5.canvasFramed.shopify) : null;
  trace('phase1_5-received', {
    phase: phase1_5 && phase1_5.phase,
    canvasShopifyKeys: pCanvasKeys,
    canvasFramedShopifyKeys: pFramedKeys,
    sampleCanvasVariant: phase1_5 && phase1_5.canvas && phase1_5.canvas.shopify && phase1_5.canvas.shopify['8x10'] && phase1_5.canvas.shopify['8x10'].shopifyVariantId,
    sampleFramedVariant: phase1_5 && phase1_5.canvasFramed && phase1_5.canvasFramed.shopify && phase1_5.canvasFramed.shopify['8x10'] && phase1_5.canvasFramed.shopify['8x10'].variantByColor && phase1_5.canvasFramed.shopify['8x10'].variantByColor.White,
  });
  log('updateResultPhase1_5: applying', {
    generationId: R.generationId,
    finalStatus: phase1_5 && phase1_5.phase,
    hasCanvasShopify: !!(phase1_5 && phase1_5.canvas && phase1_5.canvas.shopify),
    hasCanvasFramedShopify: !!(phase1_5 && phase1_5.canvasFramed && phase1_5.canvasFramed.shopify),
  });
  // 2026-05-13 night — MERGE not OVERWRITE. Phase 1.5 may return
  // `phase: 'phase1_pending_reconcile'` with PARTIAL canvas/canvasFramed
  // data (its internal Shopify-poll budget got exhausted before all
  // variants resolved). If we blindly overwrite here, we can regress
  // state that browser-side polling had ALREADY filled in completely.
  // Only fill in variants that are missing locally; never wipe a known-
  // good variant with a stub.
  if (phase1_5 && phase1_5.canvas && phase1_5.canvas.shopify) {
    var pCanvas = phase1_5.canvas.shopify;
    R.canvasShopify = R.canvasShopify || {};
    for (var ps in pCanvas) {
      if (pCanvas[ps] && pCanvas[ps].shopifyVariantId &&
          (!R.canvasShopify[ps] || !R.canvasShopify[ps].shopifyVariantId)) {
        R.canvasShopify[ps] = Object.assign({}, R.canvasShopify[ps] || {}, pCanvas[ps]);
      }
    }
  }
  if (phase1_5 && phase1_5.canvasFramed && phase1_5.canvasFramed.shopify) {
    var pFramed = phase1_5.canvasFramed.shopify;
    R.canvasFramedShopify = R.canvasFramedShopify || {};
    for (var pfs in pFramed) {
      var pfGot = pFramed[pfs];
      if (!pfGot || !pfGot.shopifyProductId) continue;
      R.canvasFramedShopify[pfs] = R.canvasFramedShopify[pfs] || { variantByColor: {} };
      R.canvasFramedShopify[pfs].shopifyProductId = R.canvasFramedShopify[pfs].shopifyProductId || pfGot.shopifyProductId;
      R.canvasFramedShopify[pfs].shopifyHandle = R.canvasFramedShopify[pfs].shopifyHandle || pfGot.shopifyHandle;
      if (pfGot.variantByColor) {
        for (var pfc in pfGot.variantByColor) {
          if (pfGot.variantByColor[pfc] && !R.canvasFramedShopify[pfs].variantByColor[pfc]) {
            R.canvasFramedShopify[pfs].variantByColor[pfc] = pfGot.variantByColor[pfc];
          }
        }
      }
    }
  }
  trace('phase1_5-applied', {
    canvasShopifyKeys: R.canvasShopify ? Object.keys(R.canvasShopify) : null,
    canvasFramedShopifyKeys: R.canvasFramedShopify ? Object.keys(R.canvasFramedShopify) : null,
    sampleCanvasVariant: R.canvasShopify && R.canvasShopify['8x10'] && R.canvasShopify['8x10'].shopifyVariantId,
    sampleFramedVariant: R.canvasFramedShopify && R.canvasFramedShopify['8x10'] && R.canvasFramedShopify['8x10'].variantByColor && R.canvasFramedShopify['8x10'].variantByColor.White,
  });
  // Re-bind ATC variant ID + form properties to whatever the active selection
  // resolves to now. updatePdpVariantState reads getCurrentVariantId which
  // looks up canvas/canvas_framed/paint_kit state.
  if (typeof updatePdpVariantState === 'function') updatePdpVariantState();
  if (typeof refreshAtcReadyState === 'function') refreshAtcReadyState();
  // Persist updated snapshot to LocalStorage so refresh / multi-gen switch
  // sees the resolved variants.
  if (typeof persistCurrentGen === 'function') persistCurrentGen();
}

// ─── updateResultPhase2 ──────────────────────────────────────────────────────
// Monolith lines 3117–3237. Applies Phase 2 data (real paint-kit composite +
// B&W breakdown) to open result modal. Cross-fades placeholders to real images.

export function updateResultPhase2(phase2) {
  var R = window.WC._currentResult;
  if (!R) {
    log('updateResultPhase2: WC._currentResult is null; skipping');
    return;
  }
  var modal = getModal();
  if (!modal) {
    log('updateResultPhase2: modal not found; skipping');
    return;
  }
  log('updateResultPhase2: applying', {
    generationId: R.generationId,
    hadPaintKit: !!(phase2.mockups && phase2.mockups.paintKit),
    hadPbnBw: !!(phase2.mockups && phase2.mockups.pbnBw),
    hadPbnColored: !!(phase2.mockups && phase2.mockups.pbnColored),
    hadCanvasShopify: !!(phase2.canvas && phase2.canvas.shopify),
    hadPaintKitShopify: !!phase2.paintKit,
  });

  if (phase2.mockups) {
    if (phase2.mockups.paintKit)   R.paintKitMockup = phase2.mockups.paintKit;
    if (phase2.mockups.pbnColored) R.pbnColoredUrl  = phase2.mockups.pbnColored;
    if (phase2.mockups.pbnBw)      R.pbnBwUrl       = phase2.mockups.pbnBw;
  }
  // Phase 1 of the new orchestrator already populates canvasShopify; this
  // line is a defensive no-op if the response also includes it.
  if (phase2.canvas && phase2.canvas.shopify) R.canvasShopify = phase2.canvas.shopify;
  if (phase2.paintKit) R.paintKitShopify = phase2.paintKit;

  // Cross-fade the paint-kit composite slide (#2) and the B&W breakdown (#3)
  // from placeholder to real. We also call refreshMockupImages() below to
  // catch the thumbnails + any slot the cross-fade misses (idempotent).
  var pkKitImg = modal.querySelector('[data-wc-element="slide-pk-kit"]');
  var pkBwImg  = modal.querySelector('[data-wc-element="slide-pk-bw"]');
  var prefersReduced = window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  [
    [pkKitImg, R.paintKitMockup],
    [pkBwImg,  R.pbnBwUrl],
  ].forEach(function (pair) {
    var el = pair[0], url = pair[1];
    if (!el || !url) return;
    // Skip cross-fade if the URL is unchanged (no point flickering).
    if (el.src === url) return;
    if (prefersReduced) { el.src = url; return; }
    el.style.transition = 'opacity 200ms ease-out';
    el.style.opacity = '0';
    setTimeout(function () {
      el.src = url;
      el.style.opacity = '1';
    }, 200);
  });

  // Repaint ALL slots (slides + thumbnails for both decks) so nothing is
  // left on the placeholder. setSrc is idempotent — a no-op if the src is
  // already correct.
  refreshMockupImages();

  // Hide any spinner/loading overlay on the placeholder slides
  modal.querySelectorAll('[data-wc-element="phase2-loading"]').forEach(function (el) {
    el.hidden = true;
  });

  // Pricing display may have been showing "—" while variant IDs were
  // missing; refresh now that they're populated.
  if (typeof refreshPricingDisplay === 'function') refreshPricingDisplay();

  // Replay any pending ATC clicks that were queued during Phase 1
  if (window.WC._pendingAtcReplay) {
    var fn = window.WC._pendingAtcReplay;
    window.WC._pendingAtcReplay = null;
    try { fn(); } catch (e) { log('pendingAtc replay error', e && e.message); }
  }

  // Persist the updated mockup URLs everywhere a later reopen/gen-switch
  // could read from:
  //   1. WC._lastResult.snapshot — used by reopenFromStateB to rehydrate R
  //   2. WC._generations[matching] — used by switchToGen + the gens strip
  //   3. WC.gens LocalStorage — survives page reloads
  try {
    if (window.WC._lastResult && window.WC._lastResult.snapshot &&
        window.WC._lastResult.generationId === R.generationId) {
      var s = window.WC._lastResult.snapshot;
      if (R.paintKitMockup)  s.paintKitMockup  = R.paintKitMockup;
      if (R.pbnBwUrl)        s.pbnBwUrl        = R.pbnBwUrl;
      if (R.pbnColoredUrl)   s.pbnColoredUrl   = R.pbnColoredUrl;
      if (R.canvasShopify)   s.canvasShopify   = R.canvasShopify;
      if (R.paintKitShopify) s.paintKitShopify = R.paintKitShopify;
      // 2026-07-02 QA round-3 (round-1 #10) — keep the framed / straight-on
      // mockups in the stash too, so a reopen rebuilds the SAME slide decks.
      if (R.canvasFramedMockups) s.canvasFramedMockups = R.canvasFramedMockups;
      if (R.straightOnMockupUrl) s.straightOnMockupUrl = R.straightOnMockupUrl;
    }
    if (Array.isArray(window.WC._generations)) {
      for (var i = window.WC._generations.length - 1; i >= 0; i--) {
        var g = window.WC._generations[i];
        if (g && g.generationId === R.generationId) {
          g.mockups = g.mockups || {};
          if (R.paintKitMockup) g.mockups.paintKit  = R.paintKitMockup;
          if (R.pbnBwUrl)       g.mockups.pbnBw     = R.pbnBwUrl;
          if (R.pbnColoredUrl)  g.mockups.pbnColored = R.pbnColoredUrl;
          if (R.canvasShopify)   g.canvas   = { shopify: R.canvasShopify };
          if (R.paintKitShopify) g.paintKit = R.paintKitShopify;
          break;
        }
      }
    }
    // 2026-07-02 QA round-4 — direct import call (was a silent no-op via the
    // empty `window.WC.gens` object). straightOn included so a reload keeps
    // the 3rd canvas slide, matching the flows (wc77-flow-b/c handlePhase2Result).
    gensUpdateMockups(R.generationId, {
      paintKit:   R.paintKitMockup,
      pbnBw:      R.pbnBwUrl,
      pbnColored: R.pbnColoredUrl,
      straightOn: R.straightOnMockupUrl,
    });
  } catch (e) {
    log('phase2 stash update error', e && e.message);
  }

  track('phase2_landed', {
    generation_id: R.generationId,
    had_paint_kit: !!(phase2.mockups && phase2.mockups.paintKit),
    had_canvas_shopify: !!(phase2.canvas && phase2.canvas.shopify),
  });
}

// ─── refreshMockupImages ─────────────────────────────────────────────────────
// Monolith lines 5005–5095. Populates all slider <img> srcs for the active
// result. Includes the deferred preloadFramedMockups kick (via
// requestIdleCallback / setTimeout). Called synchronously from showResult.
// BUGGY: this is part of the synchronous DOM-thrash chain that causes
// the 8-15s freeze — Sub-project A rewrites it. B lifts as-is.

export function refreshMockupImages() {
  var modal = getModal();
  if (!modal) return;
  var R = window.WC._currentResult;
  if (!R) return;
  // Phase 15 — slider slots; Phase 17 — also paint thumbnails.
  var t = function (sel) { return modal.querySelector('[data-wc-element="' + sel + '"]'); };
  // 2026-06-10 — NEVER paint an empty src. `el.src = ''` resolves to the PAGE
  // URL (live-verified: "Finished art" imgs with src=<page url>, naturalWidth
  // 0 — a broken tile exactly where the customer decides on the +$30 frame).
  // Missing url → fallback → otherwise drop the attribute so the slide shows
  // its alt/background instead of a broken-image icon. Returns whether a real
  // image was painted so deck slot-counts can adapt.
  var setSrc = function (key, url, fallback) {
    var el = t(key);
    if (!el) return false;
    var u = (typeof url === 'string' && url) ? url
          : ((typeof fallback === 'string' && fallback) ? fallback : '');
    if (!u) { el.removeAttribute('src'); return false; }
    if (el.src !== u) el.src = u;
    return true;
  };
  var setObjPos = function (key, pos) {
    var el = t(key);
    if (el) el.style.objectPosition = pos || '';
  };
  var art = R.artDisplayUrl || '';
  // 2026-05-14 v7 — Modal parity with PDP slot model.
  // Paint Kit deck: 3 slots (Art / Box / B&W), Box top-aligned (matches PDP).
  // Box + B&W fall back to the liquid's placeholder assets (the pre-phase-2
  // design) so a slow phase 2 never leaves blank tiles.
  var pkPlaceholder = modal.getAttribute('data-wc-placeholder-paint-kit-composite') || '';
  var bwPlaceholder = modal.getAttribute('data-wc-placeholder-pbn-bw') || '';
  setSrc('slide-pk-art', art);
  setSrc('slide-pk-kit', R.paintKitMockup, pkPlaceholder);
  setSrc('slide-pk-bw',  R.pbnBwUrl,       bwPlaceholder);
  setSrc('thumb-pk-0',   art);
  setSrc('thumb-pk-1',   R.paintKitMockup, pkPlaceholder);
  setSrc('thumb-pk-2',   R.pbnBwUrl,       bwPlaceholder);
  setObjPos('slide-pk-kit', 'top'); // Box mockup top-aligned
  setObjPos('thumb-pk-1',   'top');
  // Canvas deck: 2 slots normally (Art / active-size Room mockup), slot 2 top-aligned.
  // When R.straightOnMockupUrl is set (canary only), a 3rd slot is shown.
  // 2026-06-10 — the deck collapses to 1 slot when no room mockup exists yet
  // (e.g. a recovered gen whose mockups haven't landed) instead of showing a
  // blank second slide.
  setSrc('slide-canvas-art', art);
  setSrc('thumb-canvas-0',   art);
  var activeCanvasMockup = '';
  if (R.canvasMockups) {
    // Slot 1 = the active size's room mockup (dynamic, matches PDP slot 2).
    activeCanvasMockup = R.canvasMockups[R.activeCanvasSize || '8x10']
      || R.canvasMockups['8x10']
      || R.canvasMockups['16x20']
      || R.canvasMockups['24x30']
      || '';
  }
  var straightOnUrl = R.straightOnMockupUrl || '';
  // re-used as the single secondary slide; art backfills only when a 3rd
  // (straight-on) slot forces this middle slide to stay visible.
  var hasCanvasMockup = setSrc('slide-canvas-8x10', activeCanvasMockup, (straightOnUrl ? art : ''));
  setSrc('thumb-canvas-1', activeCanvasMockup, (straightOnUrl ? art : ''));
  setObjPos('slide-canvas-8x10', 'top'); // matches PDP top-align spec
  setObjPos('thumb-canvas-1',    'top');
  // Slot 2 (slot-3): straight-on Printify front shot — only when the canary URL is ready.
  // 2026-05-21 — data-gated; inert for all real traffic.
  setSrc('slide-canvas-16x20', straightOnUrl);
  setSrc('thumb-canvas-2',     straightOnUrl);
  // Remaining unused slides stay clear.
  setSrc('slide-canvas-24x30', '');
  setSrc('thumb-canvas-3',     '');
  var canvasModalSlots = straightOnUrl ? 3 : (hasCanvasMockup ? 2 : 1);
  // Canvas Framed deck — 2026-05-14 v3 — 3 slides:
  //   0 = finished art (always R.artDisplayUrl, no size/color variance)
  //   1 = Front mockup for current (size, color) — also slot 1 thumb
  //   2 = Context mockup for current (size, color) — also slot 2 thumb
  // Slides 1 & 2 are driven by R.canvasFramedMockups (the 24-entry
  // map keyed `<View>_<Size>_<Color>` from wolden-framed-mockup-gen).
  // 2026-06-10 — a missing (size, color) entry no longer blanks the hero
  // (live-verified on version_d, 8x10/Black): fall back to the same view in
  // another color, then any view, then the finished art. With no framed
  // mockups at all the deck collapses to the art-only slot.
  setSrc('slide-canvas-framed-art', art);
  setSrc('thumb-canvas-framed-0',   art);
  var framedSlots = 1;
  var fm = R.canvasFramedMockups;
  if (fm) {
    var pickFramed = function (view, size, color) {
      var exact = fm[view + '_' + size + '_' + color];
      if (exact) return exact;
      var keys = Object.keys(fm);
      var sameSize = null, sameView = null;
      for (var k = 0; k < keys.length; k++) {
        if (!fm[keys[k]]) continue;
        if (!sameSize && keys[k].indexOf(view + '_' + size + '_') === 0) sameSize = fm[keys[k]];
        if (!sameView && keys[k].indexOf(view + '_') === 0) sameView = fm[keys[k]];
      }
      return sameSize || sameView || '';
    };
    var fmSize  = R.activeCanvasFramedSize  || '16x20';
    var fmColor = R.activeCanvasFramedColor || 'White';
    var frontUrl   = pickFramed('Front',   fmSize, fmColor);
    var contextUrl = pickFramed('Context', fmSize, fmColor);
    if (frontUrl || contextUrl) {
      framedSlots = 3;
      setSrc('slide-canvas-framed-front',   frontUrl,   contextUrl || art);
      setSrc('slide-canvas-framed-context', contextUrl, frontUrl   || art);
      setSrc('thumb-canvas-framed-1',       frontUrl,   contextUrl || art);
      setSrc('thumb-canvas-framed-2',       contextUrl, frontUrl   || art);
    }
  }
  setSlideDeckVisibility('canvas', canvasModalSlots); // 2 slots normally, 3 when straight-on ready
  setSlideDeckVisibility('paint_kit', 2);             // 2 slots (Art + Box)
  setSlideDeckVisibility('canvas_framed', framedSlots); // 3 slots, or art-only while mockups missing
  // Paint Kit canvas-size bullet text (still used in right column)
  var pkBulletEl = modal.querySelector('[data-wc-element="paintkit-bullet-canvas"]');
  if (pkBulletEl) {
    pkBulletEl.textContent = (typeof PAINT_KIT_CANVAS_LABEL !== 'undefined' && PAINT_KIT_CANVAS_LABEL[R.activePaintKitSize]) || '';
  }
  // 2026-05-15 — DEFER the 24-image preload so it doesn't compete for
  // the browser's per-origin connection pool during the post-response
  // synchronous render cascade. Customer's interactive responsiveness
  // matters more than instant-on color-swap; the framed mockups can
  // load in the background and most customers select a single (size,
  // color) without rapidly flicking through all 24 combinations.
  //
  // Uses requestIdleCallback when available (preferred — runs only
  // when the main thread is genuinely idle), falls back to setTimeout
  // 500ms so it definitely runs even when the browser is busy.
  var _preloadKick = function () {
    try { preloadFramedMockups(); } catch (e) { /* non-fatal */ }
  };
  if (typeof requestIdleCallback === 'function') {
    try { requestIdleCallback(_preloadKick, { timeout: 2000 }); }
    catch (e) { setTimeout(_preloadKick, 500); }
  } else {
    setTimeout(_preloadKick, 500);
  }
}

// ─── setSlideDeckVisibility (private helper) ─────────────────────────────────
// Monolith lines 5110–5132. Called by refreshMockupImages. Not exported —
// wc-selectors.js already exports it under the same name. Defined locally
// so refreshMockupImages has a reference without a circular import.

function setSlideDeckVisibility(format, slotCount) {
  var modal = getModal();
  if (!modal) return;
  var key = format === 'paint_kit' ? 'paint-kit'
          : format === 'canvas_framed' ? 'canvas-framed'
          : 'canvas';
  // Slides
  var slidesContainer = modal.querySelector('[data-wc-element="slider-track-' + key + '"]');
  if (slidesContainer) {
    var slides = slidesContainer.querySelectorAll('.wolden-customize__slide');
    for (var i = 0; i < slides.length; i++) {
      slides[i].style.display = (i < slotCount) ? '' : 'none';
    }
  }
  // Thumbs
  var thumbsEl = modal.querySelector('[data-wc-element="slider-thumbs-' + key + '"]');
  if (thumbsEl) {
    var thumbs = thumbsEl.querySelectorAll('.wolden-customize__slider-thumb');
    for (var j = 0; j < thumbs.length; j++) {
      thumbs[j].style.display = (j < slotCount) ? '' : 'none';
    }
  }
}

// ─── showResult ──────────────────────────────────────────────────────────────
// Monolith lines 5176–5375. Top-level entry called by the upload orchestrator
// response handler on art_ready.
//
// KNOWN BUGS (preserved for B; Sub-project A rewrites):
//   - Synchronous DOM thrash: 8-15s freeze on throttled CPU
//   - 24 simultaneous new Image() preloads at flip time (via refreshMockupImages)
//   - Still-firing setInterval(tickLoading) may hit removed nodes
//   - MutationObserver gallery-guard still active during flip

export async function showResult(generationId, orchResult) {
  // A completed (or recovered) gen ends the in-flight window. Clear BEFORE any
  // pill refresh so reopen logic treats this gen as DONE (shows the result, not
  // the loader). _minimized is cleared separately by the wasMinimized handoff.
  try { window.WC._genInFlight = false; } catch (e) {}
  const t0 = performance.now();
  var modal = getModal();
  if (!modal) return;
  trace('showResult-entry', {
    generationId: generationId,
    phase: orchResult && orchResult.phase,
    hasCanvasShopify: !!(orchResult && orchResult.canvas && orchResult.canvas.shopify && Object.keys(orchResult.canvas.shopify).length),
    hasCanvasFramedShopify: !!(orchResult && orchResult.canvasFramed && orchResult.canvasFramed.shopify && Object.keys(orchResult.canvasFramed.shopify).length),
    hasPaintKit: !!(orchResult && orchResult.paintKit),
    paintKitHandle: orchResult && orchResult.paintKit && orchResult.paintKit.shopifyHandle,
  });

  // ── PRE-FLIGHT BOOKKEEPING (synchronous — no DOM writes) ─────────────────
  // Phase 16 — if the customer is minimized (modal hidden, progress pill
  // showing), don't auto-pop the modal back open when generation finishes.
  var wasMinimized = window.WC._minimized === true;
  if (wasMinimized) {
    window.WC._minimized = false;
    if (typeof stopLoadingTicker === 'function') stopLoadingTicker();
    track('generation_finished_while_minimized', { generation_id: generationId });
    if (typeof refreshProgressPill === 'function') refreshProgressPill();
  }

  // Hydrate the state object from the orchestrator's response.
  // 2026-05-13 night — preserve canvas/canvas_framed state when reopening
  // the SAME gen (e.g. via "View Your Art" pill or reopenLastResult).
  var R = window.WC._currentResult;
  var wasSameGen = (R.generationId === generationId);
  R.generationId = generationId;
  R.bubbleId = orchResult.bubbleId || null;
  R.canvasMockups = orchResult.mockups && orchResult.mockups.canvas || null;
  // 24 framed mockups, keyed `<View>_<Size>_<Color>` (e.g. "Front_8x10_Black").
  var orchFramedMockups =
    (orchResult.mockups && orchResult.mockups.framed) ||
    (orchResult.canvasFramed && orchResult.canvasFramed.mockups) ||
    null;
  if (orchFramedMockups) R.canvasFramedMockups = orchFramedMockups;
  // Phase 1 returns paintKit/pbn as null — fall back to placeholder.
  // 2026-05-17 — readPlaceholderUrls is monolith-only; inline the 4-line read
  // so v2-only mode shows the PBN placeholder mockups while Phase 2 is pending.
  var ph = (function () {
    var m = document.querySelector('[data-wc-component="modal"]');
    if (!m) return { paintKitComposite: '', pbnBw: '' };
    return {
      paintKitComposite: m.getAttribute('data-wc-placeholder-paint-kit-composite') || '',
      pbnBw:             m.getAttribute('data-wc-placeholder-pbn-bw') || '',
    };
  })();
  R.paintKitMockup = (orchResult.mockups && orchResult.mockups.paintKit) || ph.paintKitComposite;
  R.pbnBwUrl = (orchResult.mockups && orchResult.mockups.pbnBw) || ph.pbnBw;
  R.pbnColoredUrl = orchResult.mockups && orchResult.mockups.pbnColored || null;
  var orchCanvasShopify = orchResult.canvas && orchResult.canvas.shopify;
  var orchCanvasFramedShopify = orchResult.canvasFramed && orchResult.canvasFramed.shopify;
  var orchHasCanvas = orchCanvasShopify && Object.keys(orchCanvasShopify).length > 0;
  var orchHasFramed = orchCanvasFramedShopify && Object.keys(orchCanvasFramedShopify).length > 0;
  if (!wasSameGen) {
    R.canvasShopify = orchCanvasShopify || null;
    R.canvasFramedShopify = orchCanvasFramedShopify || null;
  } else {
    if (orchHasCanvas) R.canvasShopify = orchCanvasShopify;
    if (orchHasFramed) R.canvasFramedShopify = orchCanvasFramedShopify;
  }
  R.paintKitShopify = orchResult.paintKit || R.paintKitShopify || null;
  R.phase2Pending = orchResult.phase === 'phase1_ready';
  // 2026-05-21 — straight-on Printify front shot (canary only; empty for all real traffic)
  if (orchResult && orchResult.straightOn && orchResult.straightOn.url) R.straightOnMockupUrl = orchResult.straightOn.url;
  trace('showResult-hydrated', {
    wasSameGen: wasSameGen,
    orchHasCanvas: orchHasCanvas,
    orchHasFramed: orchHasFramed,
    stateCanvasKeys: R.canvasShopify ? Object.keys(R.canvasShopify) : null,
    stateFramedKeys: R.canvasFramedShopify ? Object.keys(R.canvasFramedShopify) : null,
  });

  // Phase 15 — Hero art (no watermark).
  // 2026-05-27 — Gallery slide #1 (the hero "Finished art") now prefers the
  // 640px render (data[1]) over the 2K render (data[0]). The 2K file is ~1-2MB
  // and on mobile/slow links it intermittently half-loaded (operator saw the
  // top-band-only "clouds" artifact ~8-10x). The 640px file (~100-200KB) paints
  // instantly + reliably and is plenty for the in-modal frame. The full 2K is
  // kept on R.artHighResUrl and only fetched when the customer taps to zoom
  // (see the zoom-current-slide handler in wcNN-selectors.js — Amazon/Etsy
  // pattern: fast thumbnail inline, hi-res on demand).
  var data = orchResult.data || [];
  R.artDisplayUrl = (data[1] && data[1].s3_url) || (data[0] && data[0].s3_url)
                || (data[1] && data[1].display_url) || (data[0] && data[0].display_url) || '';
  R.artHighResUrl = (data[0] && data[0].s3_url) || (data[0] && data[0].display_url)
                || R.artDisplayUrl || '';

  // Phase 5d: capture petImageUrl + productCtx for regenerate flow.
  var lastUpload = window.WC._uploads && window.WC._uploads[window.WC._uploads.length - 1];
  if (lastUpload) {
    R.petImageUrl = lastUpload.publicUrl;
    R.productCtx = lastUpload.product;
  }
  R.additionalPhotoUrls = [null, null];

  // Default format — Canvas Print on a NEW generation so the first price the
  // customer sees matches the $44.97 canvas anchor on the PDP (operator
  // decision 2026-05-25). On a reopen / re-render of the SAME generation we KEEP
  // the customer's last choice (see pickRevealFormat). PBN parent → paint_kit.
  // Canvas detection uses BOTH the upload-time productCtx AND the body class set
  // server-side by Liquid (works on a hard-refreshed page where productCtx is null).
  var _pt = ((R.productCtx && R.productCtx.productType) || '').toLowerCase();
  var _ph = ((R.productCtx && R.productCtx.productHandle) || '').toLowerCase();
  var _bodyClassCanvasParent = document.body && document.body.classList && document.body.classList.contains('wc-canvas-parent');
  var _isCanvasParent = _bodyClassCanvasParent
                     || _pt.indexOf('canvas') !== -1
                     || _ph.indexOf('canvas-print') !== -1
                     || _ph.indexOf('canvas-framed') !== -1;
  R.activeFormat = pickRevealFormat(_isCanvasParent, wasSameGen, R.activeFormat);
  // Phase A v1.1 — preserve pre-selected size through generation.

  // Phase 15 — reset slider indices for both decks.
  window.WC._slider.paint_kit = 0;
  window.WC._slider.canvas = 0;

  // ── 1. STOP LOADING — before any result DOM writes ───────────────────────
  //    Tears down the loading interval so it can't fire into removed nodes.
  try { stopLoading(); } catch (e) { log('[showResult] stopLoading threw', e); }

  // ── 2. TEAR DOWN gallery-guard observer ──────────────────────────────────
  //    Reinstalled after preloads complete in idle time (step 8).
  try { teardownFramedGallerySrcGuard(); } catch (e) {}

  // ── 3. FLIP MODAL STATE — single class toggle, no layout work ────────────
  if (!wasMinimized) {
    try { setModalState('result'); } catch (e) { log('[showResult] setModalState threw', e); }
  }

  // ── 4. YIELD — let browser paint the empty result shell ──────────────────
  await raf();

  // ── 5. CHUNK 1 — static parts (class toggles + text, small DOM work) ─────
  try { refreshActiveBranches(); } catch (e) { log('[showResult] chunk1 refreshActiveBranches threw', e); }
  try { refreshSizeFramePills(); } catch (e) { log('[showResult] chunk1 refreshSizeFramePills threw', e); }
  try { refreshPricingDisplay(); } catch (e) { log('[showResult] chunk1 refreshPricingDisplay threw', e); }

  // Hero image src write + applyFramedMockupToGallery (fast, single-element).
  var imgEl = modal.querySelector('[data-wc-element="result-image"]');
  if (imgEl && R.artDisplayUrl) imgEl.src = R.artDisplayUrl;
  try { if (typeof applyFramedMockupToGallery === 'function') applyFramedMockupToGallery(); } catch (e) { /* non-fatal */ }

  // 2026-05-19 — Debug strip suppressed in production. The Generation +
  // Bubble IDs were customer-visible at the bottom of the modal; operator
  // requested removal. Leave the element in the DOM (hidden) so future
  // debug builds can re-enable by uncommenting the textContent assignment.
  var metaEl = modal.querySelector('[data-wc-element="result-meta"]');
  if (metaEl) {
    metaEl.textContent = '';
    metaEl.hidden = true;
  }

  // 2026-05-14 v7 — Modal slider defaults. Canvas + framed land on slot 2
  // (idx 1) matching the PDP gallery's defaultSlotIndex (the active-size
  // mockup, not the art). Paint Kit lands on slot 1 (idx 0 = finished
  // art) per operator spec 2026-05-17.
  // setSlideIndex is exported from wc4-selectors; no monolith bridge needed.
  setSlideIndex('paint_kit', 0);
  setSlideIndex('canvas', 1);
  setSlideIndex('canvas_framed', 1);

  await raf();

  // ── 6. CHUNK 2 — refresh mockup images (sets src on visible slot images) ─
  try { refreshMockupImages(); } catch (e) { log('[showResult] chunk2 refreshMockupImages threw', e); }

  await raf();

  // ── 7. CHUNK 3 — render unified gallery (slot model build + apply) ────────
  try { renderUnifiedGallery(); } catch (e) { log('[showResult] chunk3 renderUnifiedGallery threw', e); }

  const tFirstPaint = performance.now() - t0;

  // Champion reveal application (2026-07-01 — Version D is the BAKED default;
  // flow-ui-spec §"Bake version D"). applyToReveal resolves 'version_d'
  // synchronously and definitively — no PostHog read, no flag wait — so this
  // call IS the champion render, not an experiment: with PostHog fully blocked
  // and no ?wc_exp override, renderVersionD applies here unconditionally.
  // Runs SYNCHRONOUSLY after the gallery/selector are populated (CHUNK 3) but
  // BEFORE the idle deferral so D renders WITH the modal (the old idle()
  // placement deferred by seconds under reveal load, causing a flash-of-control).
  // A QA ?wc_exp override still routes to the historical renderers.
  try { applyToReveal(document.querySelector('[data-wc-component="modal"]'), R); }
  catch (e) { log('[showResult] applyToReveal threw', e); }

  // 2026-07-02 QA round-3 (round-1 #9) — re-assert the dialog's accessible
  // name now that ALL reveal DOM writes (chunks 1-3 + the Version D
  // projection) have landed. The name computed at the state flip (step 3)
  // can predate these writes, so without this re-sync a screen reader could
  // keep announcing the last loader phase ("Almost ready"). No state is
  // passed: the helper reads the modal's CURRENT state, so the minimized
  // path (state stays 'hidden' here) is named correctly too. Idempotent.
  try { syncModalAriaName(modal); } catch (e) { /* non-fatal */ }

  // ── 8. IDLE — everything not needed before the user can interact ──────────
  idle(function () {
    try { updatePdpVariantState(); } catch (e) { log('[showResult] idle updatePdpVariantState threw', e); }
    try { preloadFramedMockups(); } catch (e) { log('[showResult] idle preloadFramedMockups threw', e); }
    try { installFramedGallerySrcGuard(); } catch (e) {}
    // 2026-05-13 evening — start browser-side resolve-recovery polling.
    if (typeof startResolveRecoveryPolling === 'function') {
      try { startResolveRecoveryPolling(); } catch (e) { log('[showResult] idle startResolveRecoveryPolling threw', e); }
    }
    try { persistCurrentGen(); } catch (e) { log('[showResult] idle persistCurrentGen threw', e); }
    try { renderGensStrip(); } catch (e) { log('[showResult] idle renderGensStrip threw', e); }
    // Phase 9 — track result visible.
    try {
      track('result_shown', {
        generation_id: generationId,
        bubble_id: R.bubbleId,
        has_canvas_mockups: !!R.canvasMockups,
        has_paint_kit_mockup: !!R.paintKitMockup,
        has_pbn: !!R.pbnBwUrl,
        msToFirstPaint: tFirstPaint,
        reveal_experience: getActiveVariant(),
      });
    } catch (e) {}
    // Cross-cutting calls via typeof-guard (left in monolith).
    var wc = window.WC;
    try { if (typeof wc.refreshAtcReadyState === 'function') wc.refreshAtcReadyState(); } catch (e) {}
    try { if (typeof refreshAtcReadyState === 'function') refreshAtcReadyState(); } catch (e) {}
    try { if (typeof wc.applyPdpVisibility === 'function') wc.applyPdpVisibility(); } catch (e) {}
    try { if (typeof applyPdpVisibility === 'function') applyPdpVisibility(); } catch (e) {}
    try { if (typeof wc.stopResolveRecoveryPolling === 'function') wc.stopResolveRecoveryPolling(); } catch (e) {}
    try { if (typeof wc.refreshReopenPill === 'function') wc.refreshReopenPill(); } catch (e) {}
    try { refreshReopenPill(); } catch (e) {}
    // PDP upload-zone state flip (State A → State B).
    try { if (typeof initUploadZoneState === 'function') initUploadZoneState(); } catch (e) { log('[showResult] idle initUploadZoneState error', e && e.message); }
    try { if (typeof wc.applyCleanBreakDeferred === 'function') wc.applyCleanBreakDeferred('post-showResult'); } catch (e) {}
    try { flushDeferredSwap(); } catch (e) {}
  });

  // Instrumentation for perf-gate verification (Sub-project A acceptance criteria).
  window.__wcMetrics = window.__wcMetrics || {};
  window.__wcMetrics.msToFirstPaint = tFirstPaint;
  window.__wcMetrics.imagePreloadsBeforeInteractive = 1;
  window.__wcMetrics.lastShowResultAt = Date.now();
}

// ─── persistCurrentGen (module-private) ──────────────────────────────────────
// Monolith lines 5381–5428. Saves current gen to LocalStorage + fires cloud
// sync. Called from showResult and updateResultPhase1_5.

function persistCurrentGen() {
  var R = window.WC._currentResult;
  if (!R || !R.generationId) return;
  var ctx = R.productCtx || {};
  var key = getNamespaceKey(ctx);
  if (!key) return;
  var ns_type = getNamespaceType(ctx);
  var genRecord = {
    id: R.generationId,
    createdAt: new Date().toISOString(),
    imageUrl: R.artDisplayUrl,
    thumbUrl: R.artDisplayUrl, // V2: a 160px crop
    petPhotoUrl: R.petImageUrl || null,
    bubbleId: R.bubbleId || null,
    status: 'complete',
    // Snapshot of the orchestrator response so we can re-hydrate the
    // result UI (size pills, mockups, pricing, variants) when the
    // customer taps a thumbnail to switch back to a prior gen.
    snapshot: {
      canvasMockups: R.canvasMockups || null,
      paintKitMockup: R.paintKitMockup || null,
      pbnBwUrl: R.pbnBwUrl || null,
      pbnColoredUrl: R.pbnColoredUrl || null,
      canvasShopify: R.canvasShopify || null,
      canvasFramedShopify: R.canvasFramedShopify || null,
      paintKitShopify: R.paintKitShopify || null,
      // 2026-07-02 QA round-3 (round-1 #10) — these two were never
      // snapshotted, so a rehydrated reveal (reload / gen switch / State-B
      // reopen) rebuilt SMALLER slide decks than the fresh reveal: no
      // straight-on slot on the canvas deck (2 slides instead of 3) and an
      // art-only canvas-framed deck (1 slide instead of 3).
      canvasFramedMockups: R.canvasFramedMockups || null,
      straightOnMockupUrl: R.straightOnMockupUrl || null,
    },
  };
  gensAdd(
    key,
    ns_type,
    ctx.productHandle || null,
    ctx.productType || null,
    genRecord
  );

  // Tier 2 (cross-device) — fire-and-forget cloud sync. No-op when the
  // gen-store URL isn't configured or the customer isn't signed in.
  try {
    var syncPayload = Object.assign({}, genRecord, {
      namespace_key: key,
      namespace_type: ns_type,
      entry_product_handle: ctx.productHandle || '',
      entry_product_type: ctx.productType || '',
    });
    syncSaveToCloud(syncPayload);
  } catch (e) { log('cloud sync save failed', e && e.message); }

  // 2026-05-19 — Trigger initUploadZoneState so the parent PDP drop zone
  // (if still rendered) flips into State B now that a gen is persisted.
  try {
    if (window.WC && typeof window.WC.initUploadZoneState === 'function') {
      window.WC.initUploadZoneState();
    }
  } catch (e) { /* non-fatal */ }
}

// ─── renderGensStrip (module-private) ────────────────────────────────────────
// Monolith lines 5432–5493. Renders the multi-gen thumbnail strip in the modal.
// No-op when 0–1 gens (per spec §7.1 — strip hidden until 2+ exist).

function renderGensStrip() {
  var modal = getModal();
  if (!modal) return;
  var R = window.WC._currentResult;
  var ctx = (R && R.productCtx) || {};
  var key = window.WC.gens.getNamespaceKey(ctx);

  var stripEl = modal.querySelector('[data-wc-element="gens-strip"]');
  var trackEl = modal.querySelector('[data-wc-element="gens-strip-track"]');
  var bannerEl = modal.querySelector('[data-wc-element="gens-banner"]');
  if (!stripEl || !trackEl) return;

  var gens = key ? window.WC.gens.list(key) : [];

  if (gens.length < 2) {
    stripEl.hidden = true;
    return;
  }
  stripEl.hidden = false;

  // 24h banner — shown for unknown users only. Auth wiring pending.
  if (bannerEl) bannerEl.hidden = !!(window.WC.auth && window.WC.auth.signedIn);

  // Render thumbs
  var activeId = (R && R.generationId) || null;
  trackEl.innerHTML = '';
  gens.forEach(function (gen) {
    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'wolden-customize__gen-thumb' +
      (gen.id === activeId ? ' wolden-customize__gen-thumb--active' : '');
    btn.setAttribute('data-wc-action', 'select-gen');
    btn.setAttribute('data-wc-gen-id', gen.id);
    btn.setAttribute('role', 'tab');
    btn.setAttribute('aria-selected', gen.id === activeId ? 'true' : 'false');
    btn.setAttribute('aria-label', 'Your ' + window.WC.gens.ordinalLabel(gen.ordinal) + ' generation');
    var ord = window.WC.gens.ordinalLabel(gen.ordinal);
    var img = document.createElement('img');
    img.src = gen.thumbUrl || gen.imageUrl;
    img.alt = '';
    img.loading = 'lazy';
    img.decoding = 'async';
    var ordEl = document.createElement('span');
    ordEl.className = 'wolden-customize__gen-thumb-ord';
    ordEl.textContent = ord;
    btn.appendChild(img);
    btn.appendChild(ordEl);
    trackEl.appendChild(btn);
  });

  // V1 Slice 3 — "+ New" tile at the right end of the strip.
  // Tap → opens the regenerate confirm dialog. Spec §8.
  var addTile = document.createElement('button');
  addTile.type = 'button';
  addTile.className = 'wolden-customize__gen-thumb wolden-customize__gen-thumb--add';
  addTile.setAttribute('data-wc-action', 'open-regen-confirm');
  addTile.setAttribute('aria-label', 'Generate another version of your art');
  addTile.innerHTML =
    '<span class="wolden-customize__gen-thumb-add-plus" aria-hidden="true">+</span>' +
    '<span class="wolden-customize__gen-thumb-add-label">New</span>';
  trackEl.appendChild(addTile);
}

// ─── switchToGen ─────────────────────────────────────────────────────────────
// Monolith lines 5496–5580. Cross-fades the hero and re-hydrates state when the
// customer taps a gen thumbnail to switch between prior generations.

export function switchToGen(genId) {
  var R = window.WC._currentResult;
  var ctx = (R && R.productCtx) || {};
  var key = window.WC.gens.getNamespaceKey(ctx);
  if (!key) return;
  var data = window.WC.gens.read(key);
  if (!data) return;
  var gen = null;
  for (var i = 0; i < data.gens.length; i++) {
    if (data.gens[i].id === genId) { gen = data.gens[i]; break; }
  }
  if (!gen) return;
  if (gen.id === R.generationId) return; // already active

  window.WC.gens.setActive(key, genId);

  // Cross-fade the hero (slider's slide #1, both decks)
  var modal = getModal();
  if (modal) {
    var heroSlots = [
      '[data-wc-element="slide-pk-art"]',
      '[data-wc-element="slide-canvas-art"]',
    ];
    heroSlots.forEach(function (sel) {
      var el = modal.querySelector(sel);
      if (!el) return;
      var prefersReduced = window.matchMedia &&
        window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (prefersReduced) {
        el.src = gen.imageUrl;
        return;
      }
      el.style.transition = 'opacity 200ms ease-out';
      el.style.opacity = '0';
      setTimeout(function () {
        el.src = gen.imageUrl;
        el.style.opacity = '1';
      }, 200);
    });
  }

  // Re-hydrate from snapshot
  var snap = gen.snapshot || {};
  R.generationId = gen.id;
  R.bubbleId = gen.bubbleId || null;
  R.artDisplayUrl = gen.imageUrl;
  R.petImageUrl = gen.petPhotoUrl || R.petImageUrl;
  if (snap.canvasMockups)       R.canvasMockups       = snap.canvasMockups;
  if (snap.paintKitMockup)      R.paintKitMockup      = snap.paintKitMockup;
  if (snap.pbnBwUrl)            R.pbnBwUrl            = snap.pbnBwUrl;
  if (snap.pbnColoredUrl)       R.pbnColoredUrl       = snap.pbnColoredUrl;
  // 2026-05-13 night — explicit reset before hydrate. The old `if (snap.X)
  // R.X = snap.X` pattern silently kept the PREVIOUS gen's variants when
  // the new gen's snapshot was missing canvasShopify (e.g. polling never
  // completed for it). Result: ATC bound to wrong product. Always reset
  // to null first, then hydrate from snapshot if present.
  R.canvasShopify       = snap.canvasShopify       || null;
  R.canvasFramedShopify = snap.canvasFramedShopify || null;
  R.paintKitShopify     = snap.paintKitShopify     || null;
  // 2026-07-02 QA round-3 (round-1 #10) — hydrate the framed / straight-on
  // mockups too (reset-first, same rationale as the variants above: never
  // leak the PREVIOUS gen's framed art onto the switched-to gen's deck).
  // Without these, the rehydrated reveal built smaller slide decks than a
  // fresh generation (2 canvas slides instead of 3; art-only framed deck).
  R.canvasFramedMockups = snap.canvasFramedMockups || null;
  R.straightOnMockupUrl = snap.straightOnMockupUrl || null;
  trace('switchToGen-hydrated', {
    generationId: gen.id,
    stateCanvasKeys: R.canvasShopify ? Object.keys(R.canvasShopify) : null,
    stateFramedKeys: R.canvasFramedShopify ? Object.keys(R.canvasFramedShopify) : null,
    hasPaintKit: !!R.paintKitShopify,
  });

  // Repaint the slider images (slide #1 already cross-faded; this
  // refreshes the kit/canvas mockups in slides 2+)
  if (typeof refreshMockupImages === 'function') refreshMockupImages();
  if (typeof refreshPricingDisplay === 'function') refreshPricingDisplay();
  if (typeof applyPdpVisibility === 'function') applyPdpVisibility(); // Phase A — gen switched, ensure PDP format selector visible
  // 2026-05-13 night — sync ATC variant + button state and (re)start
  // polling so that any missing variants get filled in for the switched-to
  // gen. Without this, ATC stayed bound to the previous gen's state.
  if (typeof updatePdpVariantState === 'function') updatePdpVariantState();
  if (typeof refreshAtcReadyState === 'function') refreshAtcReadyState();
  // Stop any in-flight polling for the prior gen, then start fresh for the
  // newly-active gen. Polling itself short-circuits when variants are
  // already in state.
  if (typeof stopResolveRecoveryPolling === 'function') stopResolveRecoveryPolling();
  if (typeof startResolveRecoveryPolling === 'function') startResolveRecoveryPolling();

  renderGensStrip();
  track('gen_switched', { generation_id: gen.id, ordinal: gen.ordinal });
}

// ─── bootstrapStateFromBubble ────────────────────────────────────────────────
// Monolith lines 5744–5862. Full gen hydration for friend-shared child PDP
// visits. The /products/<handle>.js bootstrap only knows one product; Bubble
// has the FULL gen (all 3 canvas sizes, all 3 framed sizes × 4 colors, paint
// kit, PBN, hero art URL). Called from wc-hydration.js / tryHydrateChildPdpFromUrl.

export function bootstrapStateFromBubble(genIdPrefix, cb) {
  var R = window.WC._currentResult;
  if (!R) { if (cb) cb(false); return; }

  // 2026-05-19 — V1→V2 port: self-extract genIdPrefix from the URL when not
  // passed explicitly. wc17-init.js calls this with no args; the original
  // V1 path was tryHydrateChildPdpFromUrl (monolith lines 4639-4749) which
  // did the URL extraction. Without this, every friend-share visit
  // (someone pastes /products/custom-canvas-framed-806e5a0e into a fresh
  // browser) hit `!genIdPrefix` and bailed silently — R stayed empty,
  // R.canvasShopify / R.canvasFramedShopify / R.paintKitShopify all null,
  // applyCleanBreakNow bailed on `!R.generationId`, format-selector clicks
  // never swapped URL or gallery. With this branch, the friend-share path
  // hydrates from Bubble like a fresh in-session gen.
  if (!genIdPrefix) {
    if (R.generationId) { if (cb) cb(false); return; }  // already hydrated
    var _path = window.location.pathname;
    var _m = _path.match(/^\/products\/(custom-canvas-print-|custom-canvas-framed-|custom-paint-by-numbers-kit-)([^/?#]+)\/?$/i);
    if (!_m) { if (cb) cb(false); return; }
    var _prefix = _m[1].toLowerCase();
    var _rest = _m[2];
    // Default activeFormat from URL prefix so the modal opens to the
    // right tab + the gallery picks the right slot model.
    R.activeFormat = (_prefix === 'custom-canvas-print-')         ? 'canvas'
                   : (_prefix === 'custom-canvas-framed-')        ? 'canvas_framed'
                   : 'paint_kit';
    // Prefer the Liquid-rendered full generationId metafield hint (works
    // for any URL shape including legacy non-hex). Fall back to the
    // trailing hex segment in the URL.
    try {
      if (window.WC_HYDRATE_HINT && typeof window.WC_HYDRATE_HINT.generationId === 'string' && window.WC_HYDRATE_HINT.generationId.length >= 6) {
        genIdPrefix = window.WC_HYDRATE_HINT.generationId;
      }
    } catch (_) {}
    if (!genIdPrefix) {
      var _parts = _rest.split('-');
      var _hex = _parts[_parts.length - 1];
      if (/^[a-f0-9]{6,}$/i.test(_hex)) genIdPrefix = _hex;
    }
  }
  if (!genIdPrefix || genIdPrefix.length < 6) { if (cb) cb(false); return; }
  if (!window.WoldenCustomizeConfig || !window.WoldenCustomizeConfig.orchestratorUrl) {
    log('[bootstrap-bubble] orchestrator URL not configured — skipping');
    if (cb) cb(false);
    return;
  }
  log('[bootstrap-bubble] fetching gen by prefix', genIdPrefix);
  fetch(window.WoldenCustomizeConfig.orchestratorUrl, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      token:  window.WoldenCustomizeConfig.orchestratorToken,
      action: 'fetch-gen',
      generationIdPrefix: genIdPrefix,
    }),
  })
    .then(function (resp) { return resp.json().then(function (b) { return { ok: resp.ok, body: b }; }); })
    .then(function (r) {
      if (!r.ok || !r.body || !r.body.success) {
        log('[bootstrap-bubble] fetch-gen failed', r.body && r.body.error);
        if (cb) cb(false);
        return;
      }
      var data = r.body;
      trace('bootstrap-bubble-received', {
        generationId: data.generationId,
        canvasSizes: data.canvas && data.canvas.shopify ? Object.keys(data.canvas.shopify) : null,
        framedSizes: data.canvasFramed && data.canvasFramed.shopify ? Object.keys(data.canvasFramed.shopify) : null,
        hasPaintKit: !!data.paintKit,
        hasMockups: !!(data.mockups && (data.mockups.canvas || data.mockups.paintKit)),
      });
      // Fill in generationId + bubbleId
      if (!R.generationId) R.generationId = data.generationId || null;
      if (!R.bubbleId)     R.bubbleId     = data.bubbleId     || null;
      // Hero art display URL (used by the modal's main image + result pill)
      if (!R.artDisplayUrl) {
        R.artDisplayUrl = (data.art && (data.art.urlClean || data.art.urlWatermarked)) || R.artDisplayUrl || '';
      }
      // Canvas mockups (room shots for slider deck slides 2-4)
      if (data.mockups && data.mockups.canvas) {
        R.canvasMockups = R.canvasMockups || {};
        for (var s in data.mockups.canvas) {
          if (data.mockups.canvas[s] && !R.canvasMockups[s]) R.canvasMockups[s] = data.mockups.canvas[s];
        }
      }
      // PBN + paint kit composite mockup
      if (data.mockups && data.mockups.pbnColored && !R.pbnColoredUrl) R.pbnColoredUrl = data.mockups.pbnColored;
      if (data.mockups && data.mockups.pbnBw      && !R.pbnBwUrl)      R.pbnBwUrl      = data.mockups.pbnBw;
      if (data.mockups && data.mockups.paintKit   && !R.paintKitMockup) R.paintKitMockup = data.mockups.paintKit;
      // Canvas shopify state — fill missing sizes
      if (data.canvas && data.canvas.shopify) {
        R.canvasShopify = R.canvasShopify || {};
        for (var cs in data.canvas.shopify) {
          var c = data.canvas.shopify[cs];
          if (c && c.shopifyVariantId && (!R.canvasShopify[cs] || !R.canvasShopify[cs].shopifyVariantId)) {
            R.canvasShopify[cs] = Object.assign({}, R.canvasShopify[cs] || {}, c);
          }
        }
      }
      // Canvas framed shopify state — fill missing sizes + colors
      if (data.canvasFramed && data.canvasFramed.shopify) {
        R.canvasFramedShopify = R.canvasFramedShopify || {};
        for (var fs in data.canvasFramed.shopify) {
          var f = data.canvasFramed.shopify[fs];
          if (!f) continue;
          R.canvasFramedShopify[fs] = R.canvasFramedShopify[fs] || { variantByColor: {} };
          R.canvasFramedShopify[fs].shopifyProductId = R.canvasFramedShopify[fs].shopifyProductId || f.shopifyProductId;
          R.canvasFramedShopify[fs].shopifyHandle    = R.canvasFramedShopify[fs].shopifyHandle    || f.shopifyHandle;
          if (f.variantByColor) {
            for (var fc in f.variantByColor) {
              if (f.variantByColor[fc] && !R.canvasFramedShopify[fs].variantByColor[fc]) {
                R.canvasFramedShopify[fs].variantByColor[fc] = f.variantByColor[fc];
              }
            }
          }
        }
      }
      // Paint kit shopify state (replaces the placeholder shape with full data)
      if (data.paintKit && !R.paintKitShopify) {
        R.paintKitShopify = data.paintKit;
      }
      // Framed mockups (24 of them, keyed `<View>_<Size>_<Color>`). Pulled
      // from either `mockups.framed` or `canvasFramed.mockups` — fetch-gen
      // returns both shapes for forward compat. Drives the modal hero +
      // PDP gallery via applyFramedMockupToGallery() on selection change.
      var framedMockupsFromFetch =
        (data.mockups && data.mockups.framed) ||
        (data.canvasFramed && data.canvasFramed.mockups) ||
        null;
      if (framedMockupsFromFetch) {
        R.canvasFramedMockups = Object.assign({}, R.canvasFramedMockups || {}, framedMockupsFromFetch);
      }
      // 2026-05-21 — Straight-on mockup: hydrate R.straightOnMockupUrl from the
      // most reliable source. Prefer the fetch-gen (Bubble) value — cache-
      // independent + always fresh, the same source the modal poll uses — then
      // fall back to the Liquid-emitted metafield hint (instant first-paint when
      // the storefront page cache is warm). No-op for real traffic / not-ready.
      try {
        if (!R.straightOnMockupUrl) {
          if (data.straightOn && data.straightOn.ready && data.straightOn.url) {
            R.straightOnMockupUrl = data.straightOn.url;
          } else if (window.WC_HYDRATE_HINT && window.WC_HYDRATE_HINT.straightOnMockupUrl) {
            R.straightOnMockupUrl = window.WC_HYDRATE_HINT.straightOnMockupUrl;
          }
        }
      } catch (_) {}
      // Repaint UI based on the now-complete state
      // 2026-05-15 — Added applyPdpVisibility + renderUnifiedGallery
      // (was missing). Previously this path populated R but never
      // repainted the PDP gallery, so shared-link visits to sized
      // child URLs (custom-canvas-print-8x10-XXXX, custom-canvas-
      // framed-16x20-XXXX) showed only the native Shopify gallery
      // and never picked up our 2/3-slot model. The race with the
      // new tryHydrateChildPdpFromUrl made it worse: this path
      // completes first, sets R.generationId, the new path bails on
      // the generationId check, neither calls renderUnifiedGallery.
      if (typeof applyPdpVisibility    === 'function') applyPdpVisibility();
      if (typeof refreshActiveBranches === 'function') refreshActiveBranches();
      if (typeof refreshSizeFramePills === 'function') refreshSizeFramePills();
      if (typeof refreshMockupImages   === 'function') refreshMockupImages();
      if (typeof refreshPricingDisplay === 'function') refreshPricingDisplay();
      if (typeof updatePdpVariantState === 'function') updatePdpVariantState();
      if (typeof refreshAtcReadyState  === 'function') refreshAtcReadyState();
      if (typeof renderUnifiedGallery  === 'function') renderUnifiedGallery();
      if (cb) cb(true);
    })
    .catch(function (e) {
      log('[bootstrap-bubble] error', e && e.message);
      if (cb) cb(false);
    });
}

// 2026-05-21 — Straight-on canvas mockup refresh hook.
// Called by the poll in wc25-upload.js once straightOn.ready=true.
// Sets R.straightOnMockupUrl and re-renders the gallery so slot-3 becomes visible.
// No-op when R is absent (inert for all real traffic until a canary URL is set).
(function () {
  window.WC = window.WC || {};
  window.WC.refreshCanvasStraightOn = function (url) {
    try {
      var R = window.WC._currentResult; if (!R) return;
      R.straightOnMockupUrl = url;
      // 2026-07-02 — persist to the saved gen snapshot so a reload keeps the
      // 3rd canvas slide (parity with the flows' straight-on poll, which calls
      // updateMockups({ straightOn }) — see wc77-flow-b.js / wc77-flow-c.js).
      try { gensUpdateMockups(R.generationId, { straightOn: url }); } catch (e) { /* non-fatal */ }
      // Re-render the gallery via the existing modal-image + PDP render path.
      if (typeof refreshMockupImages  === 'function') refreshMockupImages();
      if (typeof renderUnifiedGallery === 'function') renderUnifiedGallery();
    } catch (e) { /* non-fatal */ }
  };
})();
